import type React from 'react';

import { Image } from '@change/ds-core-ui/Image';
import { Text } from '@change/ds-core-ui/Text';
import { ArrowRightIcon } from '@change/ds-icons/ArrowRightIcon';
import { PlayArrowIcon } from '@change/ds-icons/PlayArrowIcon';
import { cn } from '@change/ds-utils';

import { LocalizeDate, useI18n } from '@change-corgi/core-react/i18n';

import type { MediaLinkPreview } from '../../api/mediaLinkPreview';

function initialsOf(outletName: string | null): string {
	if (!outletName) return '';
	// Two initials for multi-word outlets ("Associated Press" → "AP"), one otherwise, matching the
	// monogram treatment in the design.
	const words = outletName.split(/\s+/).filter(Boolean);
	return words
		.slice(0, 2)
		.map((word) => word[0])
		.join('')
		.toUpperCase();
}

const OutletMonogram: React.FC<{ outletName: string | null }> = ({ outletName }) => (
	<span
		aria-hidden="true"
		className="bg-fill-strong text-typography-inverse-strong-fixed text-size-body-xs flex h-5 w-5 shrink-0 items-center justify-center rounded-full leading-none font-bold"
	>
		{initialsOf(outletName)}
	</span>
);

/**
 * The outlet's logo, falling back to an initials monogram when iframely gave us no logo/favicon or
 * the image itself fails to load.
 */
const OutletAvatar: React.FC<{ outletName: string | null; logoUrl: string | null }> = ({ outletName, logoUrl }) => {
	const { translate } = useI18n();

	if (!logoUrl || !outletName) return <OutletMonogram outletName={outletName} />;

	return (
		<Image
			src={logoUrl}
			alt={translate('corgi.petition_gamma.media_mentions.card.outlet_logo_alt', { outlet: outletName })}
			aspectRatio="1:1"
			className="ring-stroke-weaker h-5 w-5 shrink-0 rounded-full object-contain ring-1 ring-inset"
			fallback={<OutletMonogram outletName={outletName} />}
		/>
	);
};

/**
 * Small-caps attribution line: outlet mark, outlet name and publish date.
 *
 * Renders nothing when we have neither an outlet nor a date — an empty byline row would otherwise
 * leave a stray monogram floating above the headline.
 */
export const MediaHitByline: React.FC<{ card: MediaLinkPreview }> = ({ card }) => {
	const { outletName, outletLogoUrl, publishedAt } = card;

	if (!outletName && !publishedAt) return null;

	return (
		<span className="flex items-center gap-2">
			{!!outletName && <OutletAvatar outletName={outletName} logoUrl={outletLogoUrl} />}
			<Text
				as="span"
				size="x-small"
				fontWeight="bold"
				truncate
				className="text-typography-weak tracking-[0.08em] uppercase"
			>
				{outletName}
				{!!outletName && !!publishedAt && ' · '}
				{!!publishedAt && <LocalizeDate date={publishedAt} type="date" format="medium" />}
			</Text>
		</span>
	);
};

/**
 * Article/video still. Decorative: the headline and byline already name the destination, so an alt
 * would only repeat them to a screen reader.
 *
 * `featured` steps the height up at `md` and above only — every item renders compact on mobile.
 */
export const MediaHitThumbnail: React.FC<{ card: MediaLinkPreview; featured?: boolean }> = ({
	card,
	featured = false,
}) => {
	if (!card.thumbnailUrl) return null;

	const heightClassName = featured ? 'h-30 md:h-47.5' : 'h-30';

	return (
		<span data-testid="media-hit-thumbnail" className={cn('relative block w-full overflow-hidden', heightClassName)}>
			<Image
				src={card.thumbnailUrl}
				alt=""
				className={cn('bg-fill-disabled w-full object-cover', heightClassName)}
				fallback={<span className={cn('bg-fill-disabled block w-full', heightClassName)} />}
			/>
			{card.medium === 'VIDEO' && (
				<>
					<span aria-hidden="true" className="bg-fill-weaker absolute inset-0" />
					<span
						aria-hidden="true"
						data-testid="media-hit-play-overlay"
						className="bg-fill-interactive absolute top-1/2 left-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full"
					>
						<PlayArrowIcon className="text-icon-strong-fixed" size="small" />
					</span>
				</>
			)}
		</span>
	);
};

/**
 * Destination cue at the foot of the item — "View", or "Watch on Instagram" for video, followed by
 * the design's 45°-rotated arrow.
 */
export const MediaHitCta: React.FC<{ card: MediaLinkPreview }> = ({ card }) => {
	const { translate } = useI18n();
	const { medium, outletName } = card;

	const label = (() => {
		if (medium !== 'VIDEO') return translate('corgi.petition_gamma.media_mentions.card.cta.read');
		if (!outletName) return translate('corgi.petition_gamma.media_mentions.card.cta.watch');
		return translate('corgi.petition_gamma.media_mentions.card.cta.watch_on', { outlet: outletName });
	})();

	return (
		<Text
			as="span"
			size="x-small"
			fontWeight="bold"
			className="text-typography-weak mt-auto flex items-center gap-1.5 tracking-[0.06em] uppercase"
		>
			{label}
			<ArrowRightIcon aria-hidden="true" className="shrink-0 -rotate-45" size={12} />
		</Text>
	);
};
